import last from "lodash-es/last";
import sortBy from "lodash-es/sortBy";
import { useMemo } from "react";

import { PhoneNumberMessageClass } from "~frontend/__generated__/graphql";

import { useThreadsContext } from "../ThreadsProvider";
import { CampaignNameAndId, Thread } from "./types";

export default function useContextCampaignAndContact({
  thread,
}: {
  thread: Thread;
}): {
  needsResponseInContext: boolean;
  contextCampaign: CampaignNameAndId;
  contextCampaignContactId?: string | null;
} {
  const { contextCampaignId } = useThreadsContext()!;

  const contextCampaign: CampaignNameAndId = useMemo(() => {
    if (contextCampaignId) {
      const firstMessage = thread.messages.find(
        msg => msg.campaignId === contextCampaignId && msg.campaignName
      );
      if (firstMessage) {
        return {
          id: firstMessage.campaignId!,
          name: firstMessage.campaignName!,
        };
      }
    }

    // We either don't have a context, or we have a context but the campaign ID
    // doesn't match any of the messages in the thread. This shouldn't happen,
    // but it could if the user manually changes the URL or there's a
    // weird data migration or bug.
    if (
      thread.fromOwnedPhoneNumber.messageClass !==
      PhoneNumberMessageClass.ShortCode
    ) {
      console.warn("Context campaign missing or doesn't match thread", {
        contextCampaignId,
        thread,
      });
    }

    // fallback to the last message in the thread that has a campaign ID
    const latestMessage = last(
      sortBy(thread.messages, "threadOrderTime").filter(
        msg => msg.campaignId && msg.campaignName
      )
    )!;

    return {
      id: latestMessage?.campaignId,
      name: latestMessage?.campaignName,
    };
  }, [thread, contextCampaignId]);

  const contextCampaignContactId = useMemo(() => {
    const campaignContactMessage = thread.messages.find(
      message =>
        message.campaignId === contextCampaign.id && message.campaignContactId
    );
    if (!campaignContactMessage && thread.messages.some(m => m.campaignId)) {
      throw new Error(
        `Thread has no messages from campaign ${contextCampaign.id}`
      );
    }

    return campaignContactMessage?.campaignContactId;
  }, [thread.messages, contextCampaign.id]);

  return {
    needsResponseInContext:
      thread.needsTexterResponse &&
      thread.ownedByCampaign?.id === contextCampaign.id,
    contextCampaign,
    contextCampaignContactId,
  };
}
